<template>
  <div class="trade-up__history">
    <button
      v-if="!histories.length"
      class="btn btn-primary"
    >
      <template v-if="selectedItems.length < 10">
        <svg
          class="i-pending"
          height="20"
          width="20"
        >
          <use xlink:href="/icons/sprite.svg#i-pending-spinner" />
        </svg>
        <span>
          {{ selectedItems.length }}/10
          {{ $t("items").toLocaleLowerCase() }}</span>
      </template>
      <template v-else>
        <img
          :alt="currentFullObject?.market_hash_name"
          :src="
            getImageFromWaxpeer(
              currentFullObject?.market_hash_name || '',
              '70:70',
            )
          "
        >
        <ui-price
          :class="[store.calculates.profit > 0 ? 'green' : 'red']"
          :price="store.calculates.profit"
        />
      </template>
    </button>
    <button
      v-for="history in histories"
      :key="`history_${history.market_hash_name}-${history.cost}-${history.profit}`"
      :class="{
        'btn-primary': history.updated_at === currentFullObject.updated_at,
        'btn-primary-stroke':
          history.updated_at !== currentFullObject.updated_at,
      }"
      class="btn"
      @click="chooseContract(history.updated_at)"
    >
      <button
        v-if="histories.length > 1"
        class="btn btn-red p4"
        @click.stop="removeContract(history.updated_at)"
      >
        <svg class="wh10">
          <use xlink:href="/icons/sprite.svg#i-trash" />
        </svg>
      </button>
      <template v-if="history.items.length < 10 || !history?.market_hash_name">
        <svg
          class="i-pending"
          height="20"
          width="20"
        >
          <use xlink:href="/icons/sprite.svg#i-pending-spinner" />
        </svg>
        <span>
          {{ history.items.length }}/10
          {{ $t("items").toLocaleLowerCase() }}</span>
      </template>
      <template v-else>
        <img
          v-if="history?.market_hash_name"
          :src="getImageFromWaxpeer(history.market_hash_name, '70:70')"
          alt="name"
          class="w30 h20"
        >
        <ui-price
          :class="[history.profit > 0 ? 'green' : 'red']"
          :price="history.profit || 0"
        />
      </template>
    </button>

    <button
      class="btn btn-primary-stroke btn-square px12"
      type="button"
      @click="addNewContract"
    >
      <span class="m0 fw400 f24 op07">+</span>
    </button>
  </div>
</template>

<script lang="ts" setup>
import type { TradeUpFullInfo } from '~/components/Pages/TradeUp/types';
import { useTradeUpStore } from '~/store/trade-up';

const store = useTradeUpStore();
const { currentFullObject } = storeToRefs(store);
const histories = ref([] as TradeUpFullInfo[]);
const selectedItems = computed({
  get: () => store.selectedItems,
  set: (value) => {
    store.selectedItems = value;
  },
});

onMounted(() => {
  histories.value = localStorage.getItem('trade-up-history')
    ? JSON.parse(localStorage.getItem('trade-up-history') || '')
    : ([] as TradeUpFullInfo[]);
  const active = localStorage.getItem('trade-up-active');
  if (histories.value.length && active) {
    currentFullObject.value
        = histories.value.find(el => el.updated_at === Number.parseFloat(active))
          || histories.value[histories.value.length - 1];
  }
});

function addNewContract() {
  const activeHistoryIndex = histories.value.findIndex(
    i => i.updated_at === currentFullObject.value?.updated_at,
  );

  if (activeHistoryIndex === -1) {
    histories.value.unshift({
      ...currentFullObject.value,
      cost: store.calculates.cost,
      profit: store.calculates.profit,
      updated_at: new Date().getTime(),
    });
  } else {
    histories.value.splice(activeHistoryIndex, 1, {
      ...currentFullObject.value,
      cost: store.calculates.cost,
      profit: store.calculates.profit,
    });
  }

  setTimeout(() => {
    currentFullObject.value = {
      cost: 0,
      profit: 0,
      market_hash_name: '',
      updated_at: new Date().getTime(),
      items: [],
    };
  }, 100);
}

function chooseContract(val: number) {
  if (currentFullObject.value.updated_at !== val) {
    store.possibleItems = [];
    currentFullObject.value = histories.value.find(
      el => el.updated_at === val,
    )!;
  }
}

function removeContract(val: number) {
  const removedIndex = histories.value.findIndex(el => el.updated_at === val);

  if (removedIndex !== -1) {
    histories.value = histories.value.filter(
      (_, index) => index !== removedIndex,
    );
    if (currentFullObject.value.updated_at === val) {
      const nextIndex
          = histories.value.length > removedIndex
            ? removedIndex
            : histories.value.length - 1;
      currentFullObject.value = histories.value[nextIndex];
    } else {
      localStorage.setItem('trade-up-history', JSON.stringify(histories.value));
    }
  }
}

watch(
  currentFullObject,
  () => {
    const newHistories = [...histories.value];
    const existingIndex = newHistories.findIndex(
      item => item.updated_at === currentFullObject.value.updated_at,
    );

    if (existingIndex !== -1) {
      newHistories[existingIndex] = currentFullObject.value;
    } else {
      newHistories.push(currentFullObject.value);
    }
    localStorage.setItem('trade-up-history', JSON.stringify(newHistories));
    localStorage.setItem(
      'trade-up-active',
      currentFullObject.value.updated_at.toString(),
    );
    histories.value = newHistories;
  },
  { deep: true },
);
</script>

<style lang="scss" scoped>
.trade-up__history {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  overflow: hidden;
  overflow-x: auto;
  white-space: nowrap;
  padding: 4px 2px;
  @media screen and (max-width: 989px) {
    flex-direction: row-reverse;
  }

  .btn-primary-stroke {
    position: relative;
  }

  .btn-red {
    position: absolute;
    right: -4px;
    top: -4px;
    display: none;
  }

  .btn-primary {
    border: 1px solid transparent;
    position: relative;

    .i-pending {
      animation: rotate360 2s linear infinite;
    }

    &:before {
      content: "";
      display: block;
      position: absolute;
      left: 0;
      top: 7px;
      bottom: 7px;
      width: 4px;
      border-radius: 0 4px 4px 0;
      background: var(--accent-secondary);
      animation: expandWidth 0.2s ease-in-out forwards;
    }
  }

  @keyframes expandWidth {
    0% {
      transform: translateX(-100%);
      opacity: 0;
    }
    100% {
      transform: unset;
      opacity: 1;
    }
  }

  .btn-primary,
  .btn-primary-stroke {
    &:hover {
      .btn-red {
        display: flex;
      }
    }
  }
}
</style>
